import React, { useState } from 'react';
import { Phone, Mail, MapPin, Check, ArrowRight, AlertCircle, Instagram, Facebook } from 'lucide-react';
import { Input } from '../Input';
import { Textarea } from '../Textarea';
import { Button } from '../Button';
import { SectionLabel } from './SectionLabel';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const PROJECT_TYPES = ['Interior remodel', 'Kitchen or bath', 'Exterior work', 'Cabinetry', 'Repairs', 'Other'];
export function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [projectType, setProjectType] = useState(PROJECT_TYPES[0]);
  const [values, setValues] = useState({
    name: '',
    email: '',
    phone: '',
    details: ''
  });
  const handleChange = (field: keyof typeof values) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setValues(prev => ({
    ...prev,
    [field]: event.target.value
  }));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!values.name.trim() || !values.email.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <section id="contact" className="relative w-full overflow-hidden bg-pine-950 py-24 sm:py-32" aria-labelledby="contact-heading" data-id="element-33" data-mp="6r88q7fal4en">
      <div className="hairline-grid absolute inset-0" aria-hidden="true" data-id="element-34" data-mp="goconpijo3n0" />
      <div className="absolute -right-40 top-0 h-[460px] w-[460px] rounded-full bg-pine-600/25 blur-[140px]" aria-hidden="true" data-id="element-35" data-mp="gqfn8xxrj53c" />

      <div className="relative mx-auto max-w-[1320px] px-6 lg:px-10" data-id="element-36" data-mp="po9j67rm7wpz">
        <div className="grid gap-14 lg:grid-cols-12" data-id="element-37" data-mp="lnz485luhvyz">
          <div className="lg:col-span-5" data-id="element-38" data-mp="loh4refjiwcp">
            <SectionLabel tone="light" data-id="element-39" data-mp="2rge95dpk0jy2">Contact Us</SectionLabel>
            <h2 id="contact-heading" className="mt-7 font-display text-4xl font-light leading-[1.05] text-bone-50 sm:text-5xl" data-id="element-40" data-mp="2riqlnbcjm23q">
              Tell us about your project.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-bone-100/70" data-id="element-41" data-mp="vd9ezqb27pkc">
              Share a few details and we'll follow up within one business day to
              schedule a walkthrough. No obligation, no pressure.
            </p>

            <ul className="mt-12 space-y-6" role="list" data-id="element-42" data-mp="15da3rrrp6xvx">
              <li className="flex items-center gap-4" data-id="element-43" data-mp="19dtzqs0jbvgh">
                <Phone className="h-4 w-4 shrink-0 text-pine-200" aria-hidden="true" data-id="element-44" data-mp="19d0rtnpuazwr" />
                <a href="tel:+15633809640" className="text-sm text-bone-50 transition-colors hover:text-pine-200" data-id="element-45" data-mp="1eassp78gpwd3">
                  (563) 380-9640
                </a>
              </li>
              <li className="flex items-center gap-4" data-id="element-46" data-mp="1t19uirp0eoai">
                <Mail className="h-4 w-4 shrink-0 text-pine-200" aria-hidden="true" data-id="element-47" data-mp="1t2umqanupv57" />
                <a href="mailto:TaylorCompany.co@gmail.com" className="break-all text-sm text-bone-50 transition-colors hover:text-pine-200" data-id="element-48" data-mp="1x3ob1557hbym">
                  TaylorCompany.co@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-4" data-id="element-49" data-mp="38jpmefmthmqm">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-pine-200" aria-hidden="true" data-id="element-50" data-mp="38mbrm8m8jq5o" />
                <a href="https://maps.google.com/?q=2724+Meadowbrook+Dr+SE+Cedar+Rapids+IA+52403" target="_blank" rel="noreferrer" className="text-sm leading-relaxed text-bone-100/80 transition-colors hover:text-bone-50" data-id="element-51" data-mp="33n8wpgzyz0bf">
                  2724 Meadowbrook Dr SE
                  <br data-id="element-52" data-mp="3mks6imu94myw" />
                  Cedar Rapids, IA 52403
                </a>
              </li>
            </ul>

            <div className="mt-10 flex items-center gap-3" data-id="element-53" data-mp="nus1l902bqm3">
              <a href="https://www.instagram.com/taylorcompanyy/" target="_blank" rel="noreferrer" aria-label="Taylor Company on Instagram" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-bone-50 transition-colors hover:border-white/60 hover:bg-white/5" data-id="element-54" data-mp="j3jrsbutm38e">
                <Instagram className="h-4 w-4" aria-hidden="true" data-id="element-55" data-mp="3urbg50oc3zo0" />
              </a>
              <a href="https://www.facebook.com/taylorcompany.co" target="_blank" rel="noreferrer" aria-label="Taylor Company on Facebook" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-bone-50 transition-colors hover:border-white/60 hover:bg-white/5" data-id="element-56" data-mp="mbrsch6fn9gc">
                <Facebook className="h-4 w-4" aria-hidden="true" data-id="element-57" data-mp="15am8nuljohym" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7" data-id="element-58" data-mp="1a12otmgr2hoo">
            <div className="rounded-2xl border border-white/10 bg-pine-900 p-8 sm:p-10" data-id="element-59" data-mp="1a2nht7nf1skf">
              {status === 'success' ? <div className="flex min-h-[420px] flex-col items-start justify-center" role="status" data-id="element-60" data-mp="2pswr1kz7u3z9">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bone-50" data-id="element-61" data-mp="33jjn15dldfrn">
                    <Check className="h-5 w-5 text-pine-900" aria-hidden="true" data-id="element-62" data-mp="2zj9js0h6p3gh" />
                  </span>
                  <h3 className="mt-8 font-display text-3xl font-light text-bone-50" data-id="element-63" data-mp="26uu73kliyx8b">
                    Request received
                  </h3>
                  <p className="mt-4 max-w-sm text-sm leading-relaxed text-bone-100/70" data-id="element-64" data-mp="2kr7eg0wchi92">
                    Thank you, {values.name.split(' ')[0] || 'friend'}. We'll be
                    in touch within one business day to set up a walkthrough.
                  </p>
                  <button type="button" onClick={() => {
                setStatus('idle');
                setValues({
                  name: '',
                  email: '',
                  phone: '',
                  details: ''
                });
              }} className="mt-8 text-[11px] uppercase tracking-widest2 text-bone-100/50 transition-colors hover:text-bone-50" data-id="element-65" data-mp="3r949vzcv8x67">
                    Submit another request
                  </button>
                </div> : <form onSubmit={handleSubmit} noValidate data-id="element-66" data-mp="267b8kvi2low4">
                  <fieldset className="border-0 p-0" data-id="element-67" data-mp="2a6tt3q5zbxyf">
                    <legend className="text-[11px] uppercase tracking-widest2 text-pine-200" data-id="element-68" data-mp="2a58y5zfr6d01">
                      Request a Quote
                    </legend>

                    <div className="mt-8 grid gap-5 sm:grid-cols-2" data-id="element-69" data-mp="2jy6imb4b2c1a">
                      <div className="sm:col-span-2" data-id="element-70" data-mp="1mi0q59qxwpub">
                        <label htmlFor="name" className="block text-xs uppercase tracking-[0.16em] text-bone-100/55" data-id="element-71" data-mp="1mhha5ykuobix">
                          Full name
                        </label>
                        <Input id="name" name="name" value={values.name} onChange={handleChange('name')} placeholder="Jane Taylor" className="mt-2 h-12 rounded-lg border-white/15 bg-pine-950 text-bone-50 placeholder:text-bone-100/30" data-id="element-72" data-mp="1exylbboxdo2f" />
                      </div>
                      <div data-id="element-73" data-mp="w5nlxrthc892">
                        <label htmlFor="email" className="block text-xs uppercase tracking-[0.16em] text-bone-100/55" data-id="element-74" data-mp="r8e0en97ogqi">
                          Email
                        </label>
                        <Input id="email" name="email" type="email" value={values.email} onChange={handleChange('email')} placeholder="you@email.com" className="mt-2 h-12 rounded-lg border-white/15 bg-pine-950 text-bone-50 placeholder:text-bone-100/30" data-id="element-75" data-mp="i762w7eirjxh" />
                      </div>
                      <div data-id="element-76" data-mp="1w4whstbmenx5">
                        <label htmlFor="phone" className="block text-xs uppercase tracking-[0.16em] text-bone-100/55" data-id="element-77" data-mp="1w8bz82sro91q">
                          Phone <span className="normal-case" data-id="element-78" data-mp="1n7m83cegb6uc">(optional)</span>
                        </label>
                        <Input id="phone" name="phone" type="tel" value={values.phone} onChange={handleChange('phone')} placeholder="(555) 000-0000" className="mt-2 h-12 rounded-lg border-white/15 bg-pine-950 text-bone-50 placeholder:text-bone-100/30" data-id="element-79" data-mp="18f28umuf9ji6" />
                      </div>
                    </div>

                    <div className="mt-7" data-id="element-80" data-mp="tty4ocub6tap">
                      <span className="block text-xs uppercase tracking-[0.16em] text-bone-100/55" data-id="element-81" data-mp="tuzigp2pltw4">
                        Project type
                      </span>
                      <div className="mt-3 flex flex-wrap gap-2" data-id="element-82" data-mp="13ruaqq8e2y1y">
                        {PROJECT_TYPES.map(type => {
                      const selected = type === projectType;
                      return <button key={type} type="button" aria-pressed={selected} onClick={() => setProjectType(type)} className={`rounded-full border px-4 py-2 text-xs transition-colors ${selected ? 'border-bone-50 bg-bone-50 text-pine-900' : 'border-white/15 text-bone-100/70 hover:border-white/40'}`} data-id="element-83" data-mp="1dh5lehaxcary">
                              {type}
                            </button>;
                    })}
                      </div>
                    </div>

                    <div className="mt-7" data-id="element-84" data-mp="110y164ppo1x5">
                      <label htmlFor="details" className="block text-xs uppercase tracking-[0.16em] text-bone-100/55" data-id="element-85" data-mp="10zn22i77nicd">
                        Project details
                      </label>
                      <Textarea id="details" name="details" rows={4} value={values.details} onChange={handleChange('details')} placeholder="Rooms involved, timeline, and anything else we should know." className="mt-2 rounded-lg border-white/15 bg-pine-950 text-bone-50 placeholder:text-bone-100/30" data-id="element-86" data-mp="m3xc68ywml2d" />
                    </div>

                    {status === 'error' && <p role="alert" className="mt-5 flex items-center gap-2 text-xs text-red-300" data-id="element-87" data-mp="211ji4ltyutiz">
                        <AlertCircle className="h-4 w-4" aria-hidden="true" data-id="element-88" data-mp="2638qtutpuv3w" />
                        Please add your name and email so we can reach you.
                      </p>}

                    <Button type="submit" disabled={status === 'submitting'} className="group mt-8 h-14 w-full rounded-full bg-bone-50 text-sm font-medium text-pine-900 hover:bg-white" data-id="element-89" data-mp="2ezrk585v0l10">
                      {status === 'submitting' ? 'Sending…' : <>
                          Send Request
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-90" data-mp="dlocxcjar7r5" />
                        </>}
                    </Button>
                    <p className="mt-4 text-center text-xs text-bone-100/40" data-id="element-91" data-mp="10jhsbtxglisy">
                      We respond within one business day.
                    </p>
                  </fieldset>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>;
}